import { Fragment, useEffect, useState, type ReactNode } from "react"
import { Check, Copy, Gauge, Truck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { FuelPercentButton } from "@/components/fuelReport/FuelPercentButton"
import ClipboardWriter from "@/lib/clipboardWriter"

type VehicleProps = {
  vehicleLabel: string | undefined
  vehicleCopyText: string
  vehicleCopyLabel: string
  odometerLabel: string | undefined
  fuelLevelLabel: string | undefined
  fuelLitersLabel: string | undefined
  fuelReportEnabled: boolean
  driverJid: string | undefined
  chatJid: string
}

const CopyButton = ({
  text,
  label,
  title,
}: {
  text: string
  label?: string
  title?: string
}) => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 700)
    return () => clearTimeout(timer)
  }, [copied])

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={(event) => {
        event.stopPropagation()
        ClipboardWriter.executeAndWriteResult(async () => text)
        setCopied(true)
      }}
      className="h-auto p-1 hover:bg-transparent"
      aria-label={label}
      title={title}
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <Copy className="h-4 w-4 text-gray-500" />
      )}
    </Button>
  )
}

/** The segments that are present, with a "|" between each. */
const Segments = ({
  className,
  segments,
}: {
  className: string
  segments: ReactNode[]
}) => {
  const present = segments.filter(Boolean)
  if (present.length === 0) return null
  return (
    <div className={className}>
      {present.map((segment, index) => (
        <Fragment key={index}>
          {index > 0 && <span className="text-muted-foreground">|</span>}
          {segment}
        </Fragment>
      ))}
    </div>
  )
}

const vehicleSegments = (
  {
    vehicleLabel,
    vehicleCopyText,
    vehicleCopyLabel,
    odometerLabel,
    fuelLevelLabel,
    fuelLitersLabel,
    fuelReportEnabled,
    driverJid,
    chatJid,
  }: VehicleProps,
  { truckIcon, copyTitle }: { truckIcon: boolean; copyTitle: boolean },
): ReactNode[] => [
  vehicleLabel && (
    <>
      {truckIcon && <Truck className="h-4 w-4 text-muted-foreground" />}
      {vehicleCopyText && (
        <CopyButton
          text={vehicleCopyText}
          label={vehicleCopyLabel}
          title={copyTitle ? vehicleCopyLabel : undefined}
        />
      )}
      <span className="text-muted-foreground">{vehicleLabel}</span>
    </>
  ),
  odometerLabel && (
    <>
      <Gauge className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">{odometerLabel}</span>
    </>
  ),
  (fuelLevelLabel || fuelLitersLabel) && (
    <FuelPercentButton
      fuelLevelLabel={fuelLevelLabel}
      fuelLitersLabel={fuelLitersLabel}
      driverJid={driverJid}
      chatJid={chatJid}
      enabled={fuelReportEnabled}
    />
  ),
]

/** The agent header's line: the vehicle and its telemetry. */
export const AgentContextLine = (props: VehicleProps) => (
  <Segments
    className="flex min-h-6 min-w-0 flex-wrap items-center gap-1 text-sm text-muted-foreground"
    segments={vehicleSegments(props, { truckIcon: false, copyTitle: true })}
  />
)

/** The classic header's line: the vehicle, its telemetry and the subject. */
export const ClassicContextLine = ({
  subjectText,
  ...props
}: VehicleProps & { subjectText: string | undefined }) => {
  if (!props.vehicleLabel && !subjectText) return null
  return (
    <Segments
      className="flex min-h-6 items-center gap-1 max-md:hidden"
      segments={[
        ...vehicleSegments(props, { truckIcon: true, copyTitle: false }),
        subjectText && (
          <>
            <CopyButton text={subjectText} />
            <div
              className="cursor-default truncate text-sm text-gray-500 dark:text-gray-400"
              onClick={(event) => event.stopPropagation()}
            >
              {subjectText}
            </div>
          </>
        ),
      ]}
    />
  )
}
